import TrashIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentSave from '@mui/icons-material/Save';
import {
    Box,
    Button,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import {
    Form,
    ReferenceField,
    useDelete,
    useGetMany,
    useNotify,
    useResourceContext,
    useUpdate,
} from 'react-admin';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { AntennaAvatar } from '../antenna/show/AntennaAvatar';
import { MemberAvatar } from '../member/show/MemberAvatar';
import { MemberLightCard } from '../member/show/MemberLightCard';
import { RelativeDate } from '@app/misc/RelativeDate';
import { NoteAttachments } from './NoteAttachments';
import { NoteInputs } from './NoteInputs';
import { MissionNote, MemberNote, Resource } from '@wwf/sdk';
import { memberNoteToInput, missionNoteToInput } from '@domain/notes';

export const Note = ({
    note,
}: {
    note: MissionNote | MemberNote;
    isLast: boolean;
}) => {
    const [isHover, setHover] = useState(false);
    const [isEditing, setEditing] = useState(false);
    const resource = useResourceContext() as 'memberNote' | 'missionNote';
    const notify = useNotify();

    const formRecord = useMemo(
        () =>
            resource === 'memberNote'
                ? memberNoteToInput(note as MemberNote)
                : missionNoteToInput(note as MissionNote),
        [note, resource]
    );

    const attachmentIds =
        note.attachments?.map(a => a.id).filter(Boolean) ?? [];
    const { data: attachments } = useGetMany<Resource>(
        'resource',
        {
            ids: attachmentIds,
        },
        { enabled: attachmentIds.length > 0 }
    );

    const [update, { isPending }] = useUpdate();

    const [deleteNote] = useDelete(
        resource,
        { id: note.id, previousData: note },
        {
            mutationMode: 'undoable',
            onSuccess: () => {
                notify('Note deleted', { type: 'info', undoable: true });
            },
        }
    );

    const handleDelete = () => {
        deleteNote();
    };

    const handleEnterEditMode = () => {
        setEditing(!isEditing);
    };

    const handleCancelEdit = () => {
        setEditing(false);
        setHover(false);
    };

    const handleNoteUpdate: SubmitHandler<FieldValues> = values => {
        update(
            resource,
            { id: note.id, data: values, previousData: note },
            {
                onSuccess: () => {
                    setEditing(false);
                    setHover(false);
                },
            }
        );
    };

    return (
        <Box
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            pb={1}
        >
            <Stack direction="row" spacing={1} alignItems="center" width="100%">
                {'memberId' in note && note.memberId ? (
                    <ReferenceField
                        record={note}
                        source="memberId"
                        reference="member"
                        link={false}
                    >
                        <MemberLightCard />
                    </ReferenceField>
                ) : resource === 'memberNote' ? (
                    <MemberAvatar variant="sm" />
                ) : (
                    <ReferenceField
                        source="antennaId"
                        reference="antenna"
                        link="show"
                    >
                        <AntennaAvatar width={20} height={20} />
                    </ReferenceField>
                )}
                <Typography color="text.secondary" variant="body2">
                    <Box
                        component="span"
                        sx={{
                            ml: 2,
                            visibility: isHover ? 'visible' : 'hidden',
                        }}
                    >
                        <Tooltip title="Edit note">
                            <IconButton
                                size="small"
                                onClick={handleEnterEditMode}
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete note">
                            <IconButton size="small" onClick={handleDelete}>
                                <TrashIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Typography>
                <Box flex={1}></Box>
                <Typography
                    color="textSecondary"
                    variant="body2"
                    component="span"
                >
                    <RelativeDate date={note.createdAt} />
                </Typography>
            </Stack>
            {isEditing ? (
                <Form onSubmit={handleNoteUpdate} record={formRecord}>
                    <NoteInputs edition />
                    <Box display="flex" justifyContent="flex-start" mt={1}>
                        <Button
                            type="submit"
                            color="primary"
                            variant="contained"
                            disabled={isPending}
                            startIcon={<ContentSave />}
                        >
                            Update Note
                        </Button>
                        <Button
                            sx={{ ml: 1 }}
                            onClick={handleCancelEdit}
                            color="primary"
                        >
                            Cancel
                        </Button>
                    </Box>
                </Form>
            ) : (
                <Stack
                    sx={{
                        paddingTop: '0.5em',
                        display: 'flex',
                        '& p:empty': {
                            minHeight: '0.75em',
                        },
                    }}
                >
                    {note.text
                        ?.split('\n')
                        .map((paragraph: string, index: number) => (
                            <Typography
                                component="p"
                                variant="body2"
                                lineHeight={1.5}
                                margin={0}
                                key={index}
                            >
                                {paragraph}
                            </Typography>
                        ))}

                    <NoteAttachments attachments={attachments ?? []} />
                </Stack>
            )}
        </Box>
    );
};
